// src/lib/supabase.ts
// Typed Supabase client for the SGS Connect Admin Dashboard.
// Uses the generated Database types for full end-to-end type safety.

import { createClient } from '@supabase/supabase-js'
import type { Database } from './database.types'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl) {
  throw new Error(
    'Missing VITE_SUPABASE_URL — copy .env.example to .env and fill in your Supabase project URL.'
  )
}
if (!supabaseAnonKey) {
  throw new Error(
    'Missing VITE_SUPABASE_ANON_KEY — copy .env.example to .env and fill in your Supabase anon key.'
  )
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Persist sessions in localStorage (standard for web apps)
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // Storage key prefix
    storageKey: 'sgs-admin-auth',
  },
  global: {
    headers: {
      'x-app-name': 'sgs-admin',
    },
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
})

// ── Convenience typed query helpers ──────────────────────────

/** Typed helper for profiles table */
export const profilesQuery = () => supabase.from('profiles')

/** Typed helper for events table */
export const eventsQuery = () => supabase.from('events')

/** Typed helper for sermons table */
export const sermonsQuery = () => supabase.from('sermons')

/** Typed helper for giving_records table */
export const givingQuery = () => supabase.from('giving_records')

/** Typed helper for announcements table */
export const announcementsQuery = () => supabase.from('announcements')

/** Typed helper for prayer_requests table */
export const prayerQuery = () => supabase.from('prayer_requests')

/** Typed helper for small_groups table */
export const groupsQuery = () => supabase.from('small_groups')

/** Typed helper for attendance_records table */
export const attendanceQuery = () => supabase.from('attendance_records')

/** Typed helper for church_settings table */
export const settingsQuery = () => supabase.from('church_settings')

/** Typed helper for sermon_series table */
export const seriesQuery = () => supabase.from('sermon_series')

/** Typed helper for testimonies table */
export const testimoniesQuery = () => supabase.from('testimonies')

/** Typed helper for event_types table */
export const eventTypesQuery = () => supabase.from('event_types')

/** Typed helper for news table */
export const newsQuery = () => supabase.from('news')
